import axios from 'axios';

export type AppErrorCode =
  | 'UNAUTHORIZED' // 401
  | 'FORBIDDEN' // 403
  | 'NOT_FOUND' // 404
  | 'VALIDATION_ERROR' // 422
  | 'RATE_LIMITED' // 429
  | 'TIMEOUT' // request exceeded its deadline (axios ECONNABORTED/ETIMEDOUT)
  | 'ABORTED' // caller cancelled (AbortController / axios CanceledError)
  | 'SUPERSEDED' // a newer request replaced this one (stale-run rejection)
  | 'PROVIDER_FAILURE' // upstream AI/model provider failed mid-run
  | 'INVALID_OUTPUT' // provider output failed schema/coherence validation
  | 'SERVER_ERROR' // 500+
  | 'NETWORK_ERROR' // Axios network issues
  | 'UNKNOWN_ERROR';

export interface AppError {
  code: AppErrorCode;
  message: string;
  originalError?: unknown;
  status?: number;
  // WHY: Lets callers (retry policy, transport, UI) distinguish transient failures
  // (rate-limit, timeout, 5xx) from terminal ones (auth, validation, abort) without
  // re-inspecting the raw transport error. Only auth/validation errors must never retry.
  retryable?: boolean;
}

export function isAppError(error: unknown): error is AppError {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  return 'code' in error && 'message' in error;
}

// WHY: A cancelled fetch/XHR surfaces as a DOMException (or plain object) whose `name`
// is 'AbortError'. Checked structurally so it works in both jsdom and node test envs
// without depending on the DOMException global.
function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

/**
 * Normalizes diverse transport errors (Axios, Network, Runtime) into a stable AppError structure.
 */
export function normalizeAppError(error: unknown): AppError {
  // --- Cancellation (Abort) — not retryable ---
  // CRITICAL: Check before the network branch. A cancel is a deliberate user/lifecycle
  // action, not a failure, and must never be surfaced as a provider/network error.
  if (axios.isCancel(error) || isAbortError(error)) {
    return {
      code: 'ABORTED',
      message: 'The request was cancelled.',
      retryable: false,
      originalError: error
    };
  }

  // --- Axios Handling ---
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;

    // Timeout has no HTTP response; axios flags it via ECONNABORTED/ETIMEDOUT.
    if (!status && (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT')) {
      return {
        code: 'TIMEOUT',
        message: 'The request timed out. Please try again.',
        retryable: true,
        originalError: error
      };
    }

    if (!status) {
      return {
        code: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection.',
        originalError: error
      };
    }

    if (status === 401) {
      return {
        code: 'UNAUTHORIZED',
        message: 'Your session has expired. Please log in again.',
        status,
        originalError: error
      };
    }

    if (status === 403) {
      return {
        code: 'FORBIDDEN',
        message: "You don't have permission to perform this action.",
        status,
        originalError: error
      };
    }

    if (status === 404) {
      return {
        code: 'NOT_FOUND',
        message: 'The requested resource was not found.',
        status,
        originalError: error
      };
    }

    if (status === 400 || status === 422) {
      return {
        code: 'VALIDATION_ERROR',
        message: error.response?.data?.message || 'The provided data was invalid.',
        status,
        originalError: error
      };
    }

    if (status === 429) {
      return {
        code: 'RATE_LIMITED',
        message: 'Too many requests. Please wait a moment and try again.',
        status,
        retryable: true,
        originalError: error
      };
    }

    if (status >= 500) {
      return {
        code: 'SERVER_ERROR',
        message: 'Our servers are having trouble. Please try again later.',
        status,
        retryable: true,
        originalError: error
      };
    }
  }

  // --- Runtime / Generic Handling ---
  const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
  return {
    code: 'UNKNOWN_ERROR',
    message,
    originalError: error
  };
}
